import { useState, useCallback } from 'react';
import { getAmplifyClient } from '../../../lib/amplifyClient';

export interface HourlyDataPoint {
  hour: number;
  timestamp: string;
  consumption: number;
  isImported: boolean;
  completeness: number;
}

export interface HourlyDataResult {
  success: boolean;
  yearMonth: string;
  hourlyData: HourlyDataPoint[];
  weekdayPattern: number[];
  weekendPattern: number[];
  totalConsumption: number;
  avgDailyConsumption: number;
  importedHoursCount: number;
  tariffType: string | null;
  isExtrapolated: boolean;
  message?: string;
}

export function useGetHourlyData() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const getHourlyData = useCallback(async (yearMonth: string): Promise<HourlyDataResult> => {
    setLoading(true);
    setError(null);

    try {
      const client = getAmplifyClient();
      const input = { yearMonth };

      const { data, errors } = await client.queries.getHourlyData({
        input: JSON.stringify(input),
      });

      if (errors && errors.length > 0) {
        throw new Error(errors.map((e: { message: string }) => e.message).join(', '));
      }

      if (!data) {
        throw new Error('No data returned from getHourlyData query');
      }

      // Parse AWSJSON response
      const result = typeof data === 'string' ? JSON.parse(data) : data;

      if (!result.success) {
        throw new Error(result.message || 'Failed to get hourly data');
      }

      setLoading(false);
      return result as HourlyDataResult;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to get hourly data';
      setError(errorMessage);
      setLoading(false);
      throw err;
    }
  }, []); // Empty deps - getAmplifyClient is stable

  return {
    getHourlyData,
    loading,
    error,
  };
}
